import { CreateAttachments } from 'src/generated-types/attachment'
import { IMainComponent } from 'src/generated-types/main-component'
import { QueryOptions } from 'src/generated-types/query-options'
import { ITaskAttachment } from 'src/generated-types/task-attachment'
import { taskQueryKeys } from 'src/query-keys'
import { IScheduledTask, IScheduledTaskListItem } from '../generated-types/scheduledTask'
import { queryClient } from '../query-client'
import { IPaginatedResult } from './paginated-result'
import { useDelete, useGet, usePatch, usePost, usePut } from './reactQuery'

const baseUrl = (localityId: EntityId) => `localities/${localityId}/scheduledTask`

export const scheduledTaskEndpoints = {
  getScheduledTasks(localityId: EntityId) {
    return `${baseUrl(localityId)}`
  },
  getAllScheduledTasks(localityId: EntityId) {
    return `${baseUrl(localityId)}/all-tasks`
  },
  singleScheduledTask(localityId: EntityId, id: EntityId) {
    return `${baseUrl(localityId)}/${id}`
  },
  assignResource(localityId: EntityId, id: EntityId) {
    return `${baseUrl(localityId)}/${id}/assign-resource`
  },
  bulkAssignResources(localityId: EntityId) {
    return `${baseUrl(localityId)}/bulk-assign-vessels`
  },
  addDeviationsToWorkOrder(localityId: EntityId, id: EntityId) {
    return `${baseUrl(localityId)}/${id}/add-deviations-to-work-order`
  },
  removeDeviationFromWorkOrder(localityId: EntityId, id: EntityId) {
    return `${baseUrl(localityId)}/${id}/remove-deviation-from-work-order`
  },
  getEquipmentForWorkOrder(localityId: EntityId) {
    return `${baseUrl(localityId)}/get-equipment-for-work-order`
  },
  addEquipmentToWorkOrder(localityId: EntityId, id: EntityId) {
    return `${baseUrl(localityId)}/${id}/add-equipment-to-work-order`
  },
  removeEquipmentFromWorkOrder(localityId: EntityId, id: EntityId) {
    return `${baseUrl(localityId)}/${id}/remove-equipment-from-work-order`
  },
  getEquipmentOfScheduledWorkOrder(localityId: EntityId, scheduledTaskId?: EntityId) {
    return `${baseUrl(localityId)}/${scheduledTaskId}/get-equipment`
  },
  getOpenDeviationsCount(localityId: EntityId, id: EntityId) {
    return `${baseUrl(localityId)}/${id}/open-deviations-count`
  },
  reopen(localityId: EntityId, id: EntityId) {
    return `${baseUrl(localityId)}/${id}/reopen`
  },
  workOrderAttachments(localityId: EntityId, id: EntityId) {
    return `${baseUrl(localityId)}/${id}/attachments`
  },
}

export const invalidateScheduledTaskCache = {
  invalidateAllScheduledTasks(localityId: EntityId, queryParams?: object) {
    queryClient.invalidateQueries(taskQueryKeys.GET_ALL_SCHEDULED_TASKS(localityId, queryParams))
  },
  useSingleScheduledTask(localityId?: EntityId, taskId?: EntityId, queryParams?: object) {
    queryClient.invalidateQueries(taskQueryKeys.GET_SCHEDULED_TASK(localityId, taskId, queryParams))
  },
  useScheduledTasks(localityId?: EntityId) {
    queryClient.invalidateQueries(taskQueryKeys.GET_SCHEDULED_TASKS(localityId))
  },
  useEquipmentOfScheduledWorkOrder(localityId?: EntityId, scheduledTaskId?: EntityId) {
    queryClient.invalidateQueries(taskQueryKeys.GET_EQUIPMENT_OF_SCHEDULED_WORK_ORDER(localityId, scheduledTaskId))
  },
  useGetEquipmentForWorkOrder(scheduledTaskId?: EntityId, localityId?: EntityId) {
    queryClient.invalidateQueries(taskQueryKeys.GET_EQUIPMENT_FOR_WORK_ORDER(localityId, scheduledTaskId))
  },
  useGetEquipmentOfWorkOrder(localityId?: EntityId, scheduledTaskId?: EntityId) {
    queryClient.invalidateQueries(taskQueryKeys.GET_EQUIPMENT_OF_SCHEDULED_WORK_ORDER(localityId, scheduledTaskId))
  },
  useGetWorkOrderAttachments(localityId?: EntityId, scheduledTaskId?: EntityId) {
    queryClient.invalidateQueries(taskQueryKeys.GET_WORK_ORDER_ATTACHMENTS(localityId, scheduledTaskId))
  },
}

export const useGetActiveScheduledTasks = (
  localityId: EntityId,
  customerId: EntityId,
  queryParams: { key?: string; page?: number; pageSize?: number },
) =>
  useGet<IPaginatedResult<IScheduledTaskListItem>>(
    scheduledTaskEndpoints.getScheduledTasks(localityId),
    { customerId, ...queryParams },
    { queryKey: taskQueryKeys.GET_SCHEDULED_TASKS(localityId, { customerId, ...queryParams }) },
  )

export const useGetScheduledTask = (localityId: EntityId, id: EntityId, isTaskAttachmentsRequired = false) =>
  useGet<IScheduledTask>(
    scheduledTaskEndpoints.singleScheduledTask(localityId, id),
    { isTaskAttachmentsRequired },
    { queryKey: taskQueryKeys.GET_SCHEDULED_TASK(localityId, id) },
  )

export const useSignInScheduleTask = (localityId: EntityId, id: EntityId) =>
  usePatch(scheduledTaskEndpoints.singleScheduledTask(localityId, id))

export const useUpdateScheduledTask = (localityId: EntityId, id: EntityId) =>
  usePut(scheduledTaskEndpoints.singleScheduledTask(localityId, id), {
    onSuccess: () => {
      invalidateScheduledTaskCache.useSingleScheduledTask(localityId, id)
      invalidateScheduledTaskCache.useScheduledTasks(localityId)
      invalidateScheduledTaskCache.invalidateAllScheduledTasks(localityId)
    },
  })

export const useSaveDraftTask = (localityId: EntityId, id: EntityId) =>
  usePatch(`${scheduledTaskEndpoints.singleScheduledTask(localityId, id)}?isSaveDraft=true`)

export const useAssignResource = (localityId: EntityId, id: EntityId) =>
  usePost(scheduledTaskEndpoints.assignResource(localityId, id))

export const useBulkAssignResources = (localityId: EntityId) =>
  usePost(scheduledTaskEndpoints.bulkAssignResources(localityId))

export const useAddDeviationsToWorkOrder = (localityId: EntityId, id: EntityId) =>
  usePost(scheduledTaskEndpoints.addDeviationsToWorkOrder(localityId, id))

export const useRemoveDeviationFromWorkOrder = (localityId: EntityId, id: EntityId) =>
  useDelete(scheduledTaskEndpoints.removeDeviationFromWorkOrder(localityId, id))

export const useAddEquipmentToWorkOrder = (localityId: EntityId, id: EntityId) =>
  usePost(scheduledTaskEndpoints.addEquipmentToWorkOrder(localityId, id))

export const useRemoveEquipmentFromWorkOrder = (localityId: EntityId, id: EntityId) =>
  useDelete(scheduledTaskEndpoints.removeEquipmentFromWorkOrder(localityId, id))

export const useGetEquipmentOfWorkOrder = (localityId: EntityId, scheduledTaskId: EntityId) =>
  useGet<IMainComponent[]>(
    scheduledTaskEndpoints.getEquipmentOfScheduledWorkOrder(localityId, scheduledTaskId),
    {},
    { queryKey: taskQueryKeys.GET_EQUIPMENT_OF_SCHEDULED_WORK_ORDER(localityId, scheduledTaskId) },
  )

export const useGetEquipmentForWorkOrder = (localityId: EntityId, scheduledTaskId?: EntityId, options?: QueryOptions) =>
  useGet<IMainComponent[]>(
    scheduledTaskEndpoints.getEquipmentForWorkOrder(localityId),
    { scheduledTaskId },
    { queryKey: taskQueryKeys.GET_EQUIPMENT_FOR_WORK_ORDER(localityId, scheduledTaskId), ...options },
  )

export const useGetOpenDeviationsCount = (localityId: EntityId, scheduledTaskId: EntityId, options?: QueryOptions) =>
  useGet<{ openDeviationsCount: number }>(
    scheduledTaskEndpoints.getOpenDeviationsCount(localityId, scheduledTaskId),
    {},
    options,
  )

export const useReopenScheduledTask = (localityId: EntityId, id: EntityId) =>
  usePost(scheduledTaskEndpoints.reopen(localityId, id))

export const useDeleteScheduledTask = (localityId: EntityId) =>
  useDelete(scheduledTaskEndpoints.getScheduledTasks(localityId))

export const useGetWorkOrderAttachments = (localityId: EntityId, scheduledTaskId: EntityId, options?: QueryOptions) =>
  useGet<ITaskAttachment[]>(
    scheduledTaskEndpoints.workOrderAttachments(localityId, scheduledTaskId),
    {},
    { queryKey: taskQueryKeys.GET_WORK_ORDER_ATTACHMENTS(localityId, scheduledTaskId), ...options },
  )

export const useAddWorkOrderAttachments = (localityId: EntityId, scheduledTaskId: EntityId) =>
  usePost<IScheduledTask, CreateAttachments>(scheduledTaskEndpoints.workOrderAttachments(localityId, scheduledTaskId))

export const useRemoveWorkOrderAttachment = (localityId: EntityId, scheduledTaskId: EntityId) =>
  useDelete(scheduledTaskEndpoints.workOrderAttachments(localityId, scheduledTaskId))
